import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
} from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'

function DomainSettingsDialog({ open, onClose, domain, onSave }) {
  const initialFormData = {
    url: domain?.url || '',
    domain: domain?.domain || '',
    redirectUrl: domain?.redirectUrl || '',
    redirectPercentage: domain?.redirectPercentage || 0,
    deviceTypes: domain?.deviceTypes || ['mobile', 'tablet'],
    targetCountries: domain?.targetCountries || ['US', 'CA', 'GB', 'AU', 'NZ', 'IE'],
  };

  const [formData, setFormData] = React.useState(initialFormData);

  const isValid = React.useMemo(() => {
    const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+)(\/[^\s]*)?$/;
    const redirectUrlRegex = /^(https?:\/\/)?([a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+)(\/[^\s]*)?$/;
    
    return (
      formData.url &&
      formData.domain &&
      formData.redirectUrl &&
      formData.redirectPercentage >= 0 &&
      formData.redirectPercentage <= 100 &&
      formData.deviceTypes.length > 0 &&
      formData.targetCountries.length > 0 &&
      urlRegex.test(formData.url) &&
      redirectUrlRegex.test(formData.redirectUrl)
    );
  }, [formData]);

  React.useEffect(() => {
    if (domain) {
      setFormData({
        url: domain.url || '',
        domain: domain.domain || '',
        redirectUrl: domain.redirectUrl || '',
        redirectPercentage: domain.redirectPercentage || 0,
        deviceTypes: domain.deviceTypes || ['mobile', 'tablet'],
        targetCountries: domain.targetCountries || ['US', 'CA', 'GB', 'AU', 'NZ', 'IE'],
      });
    } else {
      setFormData({
        url: '',
        domain: '',
        redirectUrl: '',
        redirectPercentage: 0,
        deviceTypes: ['mobile', 'tablet'],
        targetCountries: ['US', 'CA', 'GB', 'AU', 'NZ', 'IE'],
      });
    }
  }, [domain]);

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name === 'redirectPercentage') {
      // Allow empty string for typing
      if (value === '') {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }))
        return
      }
      // Only update if it's a valid number between 0 and 100
      const numValue = parseFloat(value)
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }))
      }
      return
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleUrlBlur = (e) => {
    const url = e.target.value.trim()
    if (!url) return

    try {
      // Try to parse as a full URL
      const urlObj = new URL(url)
      setFormData(prev => ({
        ...prev,
        domain: urlObj.hostname
      }))
    } catch (err) {
      try {
        // Try to parse with http:// prefix
        const urlObj = new URL('http://' + url)
        setFormData(prev => ({
          ...prev,
          domain: urlObj.hostname
        }))
      } catch (err) {
        // If it's just a domain name, use it as is
        setFormData(prev => ({
          ...prev,
          domain: url
        }))
      }
    }
  }

  const handleMultiSelectChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: typeof value === 'string' ? value.split(',') : value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (domain) {
        await onSave({ id: domain._id, data: formData });
      } else {
        await onSave(formData);
      }
      onClose();
    } catch (error) {
      // Don't close the dialog on error
      console.error('Failed to save domain:', error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {domain ? 'Edit Domain Settings' : 'Add New Domain'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="URL"
              name="url"
              value={formData.url}
              onChange={handleChange}
              onBlur={handleUrlBlur}
              required
              error={formData.url !== '' && !/^(https?:\/\/)?(localhost|([a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+))(?::\d+)?(\/[^\s]*)*$/.test(formData.url)}
              helperText={formData.url !== '' && !/^(https?:\/\/)?(localhost|([a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+))(?::\d+)?(\/[^\s]*)*$/.test(formData.url) ? "Please enter a valid URL" : ""}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Domain"
              name="domain"
              value={formData.domain}
              onChange={handleChange}
              required
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Redirect URL"
              name="redirectUrl"
              value={formData.redirectUrl}
              onChange={handleChange}
              required
              error={formData.redirectUrl !== '' && !/^(https?:\/\/)?(localhost|([a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+))(?::\d+)?(\/[^\s]*)*$/.test(formData.redirectUrl)}
              helperText={formData.redirectUrl !== '' && !/^(https?:\/\/)?(localhost|([a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+))(?::\d+)?(\/[^\s]*)*$/.test(formData.redirectUrl) ? "Please enter a valid URL" : ""}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Redirect Percentage"
              name="redirectPercentage"
              type="number"
              value={formData.redirectPercentage}
              onChange={handleChange}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Device Types</InputLabel>
              <Select
                multiple
                name="deviceTypes"
                value={formData.deviceTypes}
                onChange={handleMultiSelectChange}
                renderValue={(selected) => selected.join(', ')}
              >
                <MenuItem value="desktop">Desktop</MenuItem>
                <MenuItem value="mobile">Mobile</MenuItem>
                <MenuItem value="tablet">Tablet</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Target Countries</InputLabel>
              <Select
                multiple
                name="targetCountries"
                value={formData.targetCountries}
                onChange={handleMultiSelectChange}
                renderValue={(selected) => selected.join(', ')}
              >
                <MenuItem value="US">United States</MenuItem>
                <MenuItem value="CA">Canada</MenuItem>
                <MenuItem value="GB">United Kingdom</MenuItem>
                <MenuItem value="AU">Australia</MenuItem>
                <MenuItem value="NZ">New Zealand</MenuItem>
                <MenuItem value="IE">Ireland</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {domain?.geoData && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOnIcon color="primary" />
                <Typography>
                  {[
                    domain.geoData.city,
                    domain.geoData.region,
                    domain.geoData.country,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DomainSettingsDialog 